/**
 * TosGate — Post-Login Legal Interstitial
 * ─────────────────────────────────────────
 * Shown to authenticated users whose `tosAccepted` field is falsy.
 * This catches pre-existing accounts that were created before the
 * legal infrastructure was added.
 *
 * Flow:
 *   1. Age confirmation checkbox
 *   2. CC BY-SA 4.0 informational notice
 *   3. TOS / Privacy / DMCA agreement checkbox
 *   4. "Watch the tour" video banner (optional but prominent)
 *   5. "Accept & Continue" button → calls POST /api/auth/accept-tos
 *
 * On success, calls onAccepted() which refreshes the user object
 * in AuthContext, causing ProtectedRoute to re-evaluate and let
 * the user through.
 *
 * Place at: src/components/TosGate.jsx
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, Check, Share2, Play, ArrowRight,
  Shield, AlertTriangle, X
} from 'lucide-react';
import { api } from '../api';
import { cn } from '../utils/cn';

// ============================================================================
// VIDEO TOUR MODAL
// ============================================================================

const VideoTourSection = () => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        className="w-full group flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-purple-500/10 to-indigo-500/10 border border-purple-500/20 hover:border-purple-500/40 hover:from-purple-500/15 hover:to-indigo-500/15 transition-all duration-300"
      >
        <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0 group-hover:bg-purple-500/30 group-hover:scale-105 transition-all duration-300">
          <Play className="w-6 h-6 text-purple-400 ml-0.5" />
        </div>
        <div className="text-left flex-1 min-w-0">
          <p className="text-sm font-semibold text-zinc-100 group-hover:text-white transition-colors">
            New here? Watch the 2-minute tour
          </p>
          <p className="text-xs text-zinc-500 mt-0.5">
            See how the cosmic map, forking, and challenges work
          </p>
        </div>
        <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-purple-400 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
      </button>

      {showModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setShowModal(false)}
        >
          <div
            className="w-full max-w-3xl bg-zinc-900 border border-zinc-700 rounded-2xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 16:9 video container */}
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              {/*
                ═══════════════════════════════════════════════════════
                REPLACE THIS PLACEHOLDER WITH YOUR VIDEO

                Self-hosted:
                  <video src="/videos/enume-tour.mp4" controls autoPlay
                    className="absolute inset-0 w-full h-full object-cover" />

                YouTube:
                  <iframe src="https://www.youtube.com/embed/VIDEO_ID?autoplay=1"
                    allow="autoplay; encrypted-media" allowFullScreen
                    className="absolute inset-0 w-full h-full" />

                Vimeo:
                  <iframe src="https://player.vimeo.com/video/VIDEO_ID?autoplay=1"
                    allow="autoplay; fullscreen" allowFullScreen
                    className="absolute inset-0 w-full h-full" />
                ═══════════════════════════════════════════════════════
              */}
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-zinc-800">
                <div className="w-16 h-16 rounded-2xl bg-purple-500/20 flex items-center justify-center mb-4">
                  <Play className="w-8 h-8 text-purple-400 ml-1" />
                </div>
                <p className="text-zinc-400 text-sm font-medium">Tour video coming soon</p>
                <p className="text-zinc-600 text-xs mt-1">Check back shortly</p>
              </div>
            </div>
            <div className="px-5 py-3 bg-zinc-800/50 border-t border-zinc-700/50 flex items-center justify-between">
              <p className="text-xs text-zinc-500">2 min &middot; How Enume works</p>
              <button
                onClick={() => setShowModal(false)}
                className="text-xs text-zinc-400 hover:text-white px-3 py-1.5 rounded-lg hover:bg-zinc-700/50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ============================================================================
// CUSTOM CHECKBOX
// ============================================================================

const LegalCheckbox = ({ checked, onChange, children }) => (
  <label className="flex items-start gap-3 cursor-pointer group">
    <div className="mt-0.5 flex-shrink-0">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      <div className={cn(
        'w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200',
        checked
          ? 'bg-purple-600 border-purple-600 shadow-lg shadow-purple-500/20'
          : 'border-zinc-600 group-hover:border-zinc-400'
      )}>
        {checked && <Check className="w-3.5 h-3.5 text-white" />}
      </div>
    </div>
    <span className="text-sm text-zinc-300 leading-relaxed">{children}</span>
  </label>
);

// ============================================================================
// TOS GATE
// ============================================================================

const TosGate = ({ user, onAccepted }) => {
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = ageConfirmed && agreedToTerms && !loading;

  const handleAccept = async () => {
    if (!canSubmit) return;

    setLoading(true);
    setError('');

    try {
      await api.post('/auth/accept-tos', {
        ageConfirmed: true,
        agreedToTerms: true,
      });
      onAccepted();
    } catch (err) {
      const msg = err?.response?.data?.error
        || err?.response?.data?.msg
        || err?.message
        || 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      {/* Background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-1/4 w-1/2 h-1/2 bg-purple-500/8 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-indigo-500/8 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-lg animate-in fade-in slide-in-from-bottom-4 duration-500">
        {/* Logo */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2.5">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-500/20">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-2xl font-bold text-white tracking-tight">Enume</span>
          </div>
        </div>

        {/* Card */}
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-6 md:p-8 backdrop-blur-sm shadow-2xl">
          {/* Header */}
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center">
              <Shield className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">One quick thing</h1>
            </div>
          </div>
          <p className="text-zinc-400 text-sm mb-6 leading-relaxed">
            We've updated our terms since you last signed in.
            Please review and accept to continue.
          </p>

          <div className="space-y-4">
            {/* ── Video Tour Banner ──────────────────────────────── */}
            <VideoTourSection />

            {/* ── Age Confirmation ───────────────────────────────── */}
            <LegalCheckbox checked={ageConfirmed} onChange={setAgeConfirmed}>
              I confirm I am <span className="text-white font-medium">18 years of age or older</span>
            </LegalCheckbox>

            {/* ── CC BY-SA 4.0 Notice (informational, no checkbox) ─ */}
            <div className="p-3 rounded-xl bg-purple-500/5 border border-purple-500/15">
              <div className="flex gap-2.5">
                <Share2 className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-zinc-400 leading-relaxed">
                  <span className="text-zinc-200 font-medium">Creative Commons:</span>{' '}
                  Everything you upload is licensed under{' '}
                  <a
                    href="https://creativecommons.org/licenses/by-sa/4.0/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-400 hover:text-purple-300 underline underline-offset-2"
                  >
                    CC BY-SA 4.0
                  </a>.
                  Anyone can fork, remix, and build on your work (with credit).
                  This license is irrevocable.{' '}
                  <Link
                    to="/terms#1.3"
                    target="_blank"
                    className="text-purple-400 hover:text-purple-300 underline underline-offset-2"
                  >
                    Learn more
                  </Link>
                </div>
              </div>
            </div>

            {/* ── TOS Agreement ──────────────────────────────────── */}
            <LegalCheckbox checked={agreedToTerms} onChange={setAgreedToTerms}>
              I agree to the{' '}
              <Link to="/terms" target="_blank"
                className="text-purple-400 hover:text-purple-300 underline underline-offset-2">
                Terms of Service
              </Link>,{' '}
              <Link to="/privacy" target="_blank"
                className="text-purple-400 hover:text-purple-300 underline underline-offset-2">
                Privacy Policy
              </Link>, and{' '}
              <Link to="/dmca" target="_blank"
                className="text-purple-400 hover:text-purple-300 underline underline-offset-2">
                DMCA Policy
              </Link>
            </LegalCheckbox>

            {/* ── Error ──────────────────────────────────────────── */}
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/25 rounded-xl flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            {/* ── Submit ─────────────────────────────────────────── */}
            <button
              onClick={handleAccept}
              disabled={!canSubmit}
              className={cn(
                'w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium text-sm transition-all duration-200',
                canSubmit
                  ? 'bg-purple-600 hover:bg-purple-500 text-white shadow-lg shadow-purple-500/20 hover:shadow-purple-500/30 active:scale-[0.98]'
                  : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
              )}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Accepting...
                </>
              ) : (
                <>
                  Accept & Continue
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>

          {/* Fine print */}
          <p className="mt-4 text-center text-xs text-zinc-600 leading-relaxed">
            By continuing you acknowledge you've read and understood our policies.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TosGate;